import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import BusinessCard from "../components/BusinessCard";
import MapView from "../components/MapView";
import Pagination from "../components/Pagination";
import { MapTrifold, ListBullets, Sparkle, Phone, Star, Clock } from "@phosphor-icons/react";

const LIMIT = 60;
const PREF_KEY = "mc68_directory_prefs";

function loadPrefs() {
  try {
    const raw = localStorage.getItem(PREF_KEY);
    if (!raw) return {};
    return JSON.parse(raw);
  } catch { return {}; }
}

export default function Directory() {
  const { t, lang } = useI18n();
  const locale = lang === "fr" ? "fr-FR" : lang === "de" ? "de-DE" : "en-US";
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "1", 10) || 1);
  const prefs = loadPrefs();
  const [filters, setFilters] = useState({
    city: searchParams.get("city") || "",
    category: searchParams.get("category") || "",
    premium_only: searchParams.get("premium") === "1" || prefs.premium_only || false,
    has_phone: searchParams.get("has_phone") === "1" || false,
    min_rating: parseFloat(searchParams.get("min_rating") || "0") || 0,
    open_now: searchParams.get("open_now") === "1" || false,
    q: searchParams.get("q") || "",
    sort: searchParams.get("sort") || prefs.sort || "name_asc",
  });
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [premiumStats, setPremiumStats] = useState(null);

  useEffect(() => {
    api.get("/public/premium-stats").then((r) => setPremiumStats(r.data)).catch(() => {});
  }, []);

  // Persist user preferences (NOT filter state — only persistent prefs)
  useEffect(() => {
    try {
      localStorage.setItem(PREF_KEY, JSON.stringify({
        premium_only: filters.premium_only,
        sort: filters.sort,
      }));
    } catch { /* noop */ }
  }, [filters.premium_only, filters.sort]);

  useEffect(() => {
    api.get("/public/cities").then((r) => setCities(r.data || []));
    api.get("/public/categories").then((r) => setCategories(r.data || []));
  }, []);

  // Reset to page 1 whenever filters change (but not when only the page changes)
  useEffect(() => {
    setPage(1);
  }, [filters.city, filters.category, filters.premium_only, filters.has_phone,
      filters.min_rating, filters.open_now, filters.q, filters.sort]);

  useEffect(() => {
    setLoading(true);
    const skip = (page - 1) * LIMIT;
    const params = { limit: LIMIT, skip, ...filters };
    if (!params.city) delete params.city;
    if (!params.category) delete params.category;
    if (!params.q) delete params.q;
    if (!params.has_phone) delete params.has_phone;
    if (!params.open_now) delete params.open_now;
    if (!params.min_rating) delete params.min_rating;
    api.get("/public/businesses", { params }).then((r) => {
      setItems(r.data.items);
      setTotal(r.data.total);
      setLoading(false);
    });
    // Sync URL (keeps shareable/bookmarkable links)
    const next = new URLSearchParams();
    if (filters.city) next.set("city", filters.city);
    if (filters.category) next.set("category", filters.category);
    if (filters.q) next.set("q", filters.q);
    if (filters.premium_only) next.set("premium", "1");
    if (filters.has_phone) next.set("has_phone", "1");
    if (filters.open_now) next.set("open_now", "1");
    if (filters.min_rating) next.set("min_rating", String(filters.min_rating));
    if (filters.sort && filters.sort !== "name_asc") next.set("sort", filters.sort);
    if (page > 1) next.set("page", String(page));
    setSearchParams(next, { replace: true });
  }, [filters, page, setSearchParams]);

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16" data-testid="directory-page">
      <div className="label-uppercase mb-3">{t.directory.eyebrow}</div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-medium mb-8">{t.directory.title}</h1>

      {/* Filters */}
      <div className="bg-card border border-border rounded-sm p-5 grid md:grid-cols-12 gap-3 mb-8" data-testid="directory-filters">
        <div className="md:col-span-4">
          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1 block">{t.directory.filter_city}</label>
          <select
            value={filters.city}
            onChange={(e) => setFilters({ ...filters, city: e.target.value })}
            className="w-full border border-border rounded-sm px-3 py-2 text-sm bg-background focus:border-primary outline-none"
            data-testid="filter-city"
          >
            <option value="">{t.directory.all_option}</option>
            {cities.map((c) => <option key={c.city} value={c.city}>{c.city} ({c.count})</option>)}
          </select>
        </div>
        <div className="md:col-span-4">
          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1 block">{t.directory.filter_category}</label>
          <select
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            className="w-full border border-border rounded-sm px-3 py-2 text-sm bg-background focus:border-primary outline-none"
            data-testid="filter-category"
          >
            <option value="">{t.directory.all_option}</option>
            {categories.map((c, i) => <option key={c.value || c.category || i} value={c.category}>{c.category} ({c.count})</option>)}
          </select>
        </div>
        <div className="md:col-span-3">
          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1 block">{t.directory.filter_keyword}</label>
          <input
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            placeholder={t.directory.filter_keyword_placeholder}
            className="w-full border border-border rounded-sm px-3 py-2 text-sm bg-background focus:border-primary outline-none"
            data-testid="filter-q"
          />
        </div>
        <div className="md:col-span-1 flex items-end">
          {/* Premium toggle moved next to sort controls for visibility — kept here as
              spacer to preserve the grid alignment. */}
        </div>
      </div>

      {/* VAGUE 37A — Quick-filter chips (phone / rating / open-now) */}
      <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="directory-quick-chips">
        <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mr-1">Affiner&nbsp;:</span>
        <button
          onClick={() => setFilters({ ...filters, has_phone: !filters.has_phone })}
          aria-pressed={filters.has_phone}
          className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
            filters.has_phone
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-card border-border hover:border-primary/40 hover:bg-primary/5"
          }`}
          data-testid="chip-has-phone"
        >
          <Phone size={12} weight={filters.has_phone ? "fill" : "bold"} />
          Avec téléphone
        </button>
        <button
          onClick={() => setFilters({ ...filters, min_rating: filters.min_rating ? 0 : 4 })}
          aria-pressed={filters.min_rating > 0}
          className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
            filters.min_rating > 0
              ? "bg-secondary text-secondary-foreground border-secondary"
              : "bg-card border-border hover:border-secondary/40 hover:bg-secondary/5"
          }`}
          data-testid="chip-min-rating"
        >
          <Star size={12} weight={filters.min_rating > 0 ? "fill" : "bold"} />
          4 étoiles et +
        </button>
        <button
          onClick={() => setFilters({ ...filters, open_now: !filters.open_now })}
          aria-pressed={filters.open_now}
          className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
            filters.open_now
              ? "bg-emerald-600 text-white border-emerald-600"
              : "bg-card border-border hover:border-emerald-500/40 hover:bg-emerald-50"
          }`}
          data-testid="chip-open-now"
        >
          <Clock size={12} weight={filters.open_now ? "fill" : "bold"} />
          Ouvert maintenant
        </button>
        {(filters.has_phone || filters.min_rating > 0 || filters.open_now) && (
          <button
            onClick={() => setFilters({ ...filters, has_phone: false, min_rating: 0, open_now: false })}
            className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 ml-1"
            data-testid="chip-reset"
          >
            Tout effacer
          </button>
        )}
      </div>

      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div className="text-sm text-muted-foreground flex items-center gap-3 flex-wrap">
          <span><span className="font-semibold text-foreground tabular-nums">{total.toLocaleString(locale)}</span> {t.directory.results_label}</span>
          {filters.premium_only && (
            <span className="text-[10px] uppercase tracking-widest font-bold bg-primary/10 text-primary px-2 py-0.5 rounded-sm flex items-center gap-1" data-testid="premium-active-chip">
              <Sparkle size={10} weight="fill"/> {t.directory.premium_only_chip}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Premium pill toggle — persistent + URL-synced */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setFilters({...filters, premium_only: !filters.premium_only})}
              className={`text-xs px-3 py-1.5 rounded-sm border flex items-center gap-1.5 font-semibold transition-colors ${
                filters.premium_only
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card border-border hover:bg-accent text-foreground"
              }`}
              data-testid="filter-premium-toggle"
              aria-pressed={filters.premium_only}
            >
              <Sparkle size={12} weight={filters.premium_only ? "fill" : "regular"}/>
              <span>{t.directory.premium_only_chip}</span>
            </button>
            {premiumStats && premiumStats.new_7d > 0 && (
              <span className="text-[10px] uppercase tracking-widest font-bold bg-secondary/15 text-secondary px-2 py-1 rounded-sm flex items-center gap-1" title={t.directory.premium_new_title.replace("{n}", premiumStats.new_7d).replace("{total}", premiumStats.total)} data-testid="premium-new-badge">
                {t.directory.premium_new_week.replace("{n}", premiumStats.new_7d)}
              </span>
            )}
          </div>
          <label className="text-xs text-muted-foreground flex items-center gap-2">
            {t.directory.sort_label}
            <select value={filters.sort} onChange={e => setFilters({...filters, sort: e.target.value})} className="border border-border rounded-sm px-2 py-1.5 text-xs bg-background focus:border-primary outline-none" data-testid="directory-sort">
              <option value="name_asc">{t.directory.sort_name_asc}</option>
              <option value="name_desc">{t.directory.sort_name_desc}</option>
              <option value="city_asc">{t.directory.sort_city_asc}</option>
              <option value="postal_asc">{t.directory.sort_postal_asc}</option>
              <option value="premium_first">{t.directory.sort_premium_first}</option>
              <option value="rating">{t.directory.sort_rating}</option>
              <option value="relevance">{t.directory.sort_relevance}</option>
            </select>
          </label>
          <div className="flex border border-border rounded-sm overflow-hidden">
          <button onClick={() => setView("list")} className={`px-3 py-2 text-xs font-semibold flex items-center gap-1 ${view === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="view-list"><ListBullets size={14}/> {t.directory.view_list}</button>
          <button onClick={() => setView("map")} className={`px-3 py-2 text-xs font-semibold flex items-center gap-1 ${view === "map" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="view-map"><MapTrifold size={14}/> {t.directory.view_map}</button>
        </div>
        </div>
      </div>

      {loading ? (
        <div className="text-muted-foreground">{t.directory.loading}</div>
      ) : items.length === 0 ? (
        <div className="bg-card border border-dashed border-border rounded-sm p-12 text-center text-muted-foreground" data-testid="directory-empty">{t.directory.empty}</div>
      ) : view === "list" ? (
        <>
          {/* Top pagination — only shown when there's >1 page so it stays clean */}
          {totalPages > 1 && (
            <div className="mb-6">
              <Pagination page={page} totalPages={totalPages} total={total} limit={LIMIT} onChange={setPage} testidPrefix="dir-top"/>
            </div>
          )}

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="directory-grid">
            {items.map((b) => <BusinessCard key={b.id} b={b} />)}
          </div>

          {/* Bottom pagination — always-visible primary control */}
          {totalPages > 1 && (
            <div className="mt-10">
              <Pagination page={page} totalPages={totalPages} total={total} limit={LIMIT} onChange={setPage} testidPrefix="dir-bottom"/>
            </div>
          )}
        </>
      ) : (
        <MapView businesses={items} />
      )}
    </div>
  );
}
